import {
  IdTokenClaims,
  InteractionRequiredAuthError,
  InteractionStatus,
} from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useEffect, useRef, useState } from "react";
import { apiRequest } from "../Auth/AuthConfig";

interface CustomIdTokenClaims extends IdTokenClaims {
  oid?: string;
}

const isEmbeddedApp = () => {
  try {
    const searchParams = new URLSearchParams(window.location.search);
    const embeddingParam =
      searchParams.get("embedding") || searchParams.get("embedded");

    return (
      window.self !== window.top ||
      window.location.pathname.toLowerCase().includes("/embedding") ||
      embeddingParam === "true" ||
      embeddingParam === "1"
    );
  } catch {
    return true;
  }
};

const useAccessToken = () => {
  const { accounts, instance, inProgress } = useMsal();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [userId, setUserId] = useState<string>("");
  const [userName, setUserName] = useState<string>("");
  const [error, setError] = useState<Error | null>(null);
  const refreshTimerRef = useRef<number | null>(null);
  const redirectInFlightRef = useRef(false);

  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      redirectInFlightRef.current = false;
    }
  }, [inProgress]);

  useEffect(() => {
    const embedded = isEmbeddedApp();

    const clearRefreshTimer = () => {
      if (refreshTimerRef.current !== null) {
        window.clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };

    const fetchAccessToken = async () => {
      if (!accounts.length || !instance) {
        setAccessToken(null);
        return;
      }

      if (inProgress !== InteractionStatus.None) {
        return;
      }

      const request = {
        ...apiRequest,
        account: instance.getActiveAccount() ?? accounts[0],
      };

      try {
        setError(null);

        const response = await instance.acquireTokenSilent(request);

        setAccessToken(response.accessToken);

        const claims = response.idTokenClaims as CustomIdTokenClaims;
        setUserId(claims.oid || "");
        setUserName(claims.name || claims.preferred_username || "");

        clearRefreshTimer();

        if (response.expiresOn) {
          const timeToExpire = response.expiresOn.getTime() - Date.now();
          refreshTimerRef.current = window.setTimeout(
            fetchAccessToken,
            Math.max(timeToExpire - 120000, 10000),
          );
        }
      } catch (err: unknown) {
        if (err instanceof InteractionRequiredAuthError) {
          setAccessToken(null);
          setError(err);

          if (embedded) {
            return;
          }

          if (
            inProgress === InteractionStatus.None &&
            !redirectInFlightRef.current
          ) {
            redirectInFlightRef.current = true;
            await instance.acquireTokenRedirect(request);
          }

          return;
        }

        console.error("Error fetching access token:", err);
        setAccessToken(null);
        setError(err as Error);
      }
    };

    fetchAccessToken();

    return () => {
      clearRefreshTimer();
    };
  }, [accounts, instance, inProgress]);

  return { accessToken, userId, userName, error };
};

export default useAccessToken;